// Read-only custom field cells for the Projects list's hand-built table. Each cell is a
// TdLink to the row, like every other cell on this list; values truncate to one line with the full value in
// the design-system hover box only while cut off (TruncatedValue). Cells are read-only, so there is no editability tooltip.
import React from 'react';
import Styled from 'styled-components';
import TdLink from '../../../../components/td_link';
import {
	CUSTOM_FIELD_COLUMN_WIDTH_PX,
	customFieldColumnId,
	formatCustomFieldValue,
	type CustomFieldColumnDef,
} from '../../../shared/components/custom-field-columns/customFieldColumnModel';
import {HeaderName, TruncatedValue} from '../../../shared/components/custom-field-columns/CustomFieldText';
import {projectCustomFieldDateParts} from './projectCustomFieldColumns';

export interface CustomFieldFormat {
	locale: string;
	decimalSeparator?: ',' | '.';
}

const WIDTH = `${CUSTOM_FIELD_COLUMN_WIDTH_PX}px`;
const cellCy = (definition: CustomFieldColumnDef): string => `projects-col-cf-${definition.key}-cell`;

// Label plus the native sort-indicator spacer side by side, as the native sortable headers render them.
const HeaderContent = Styled.div`
	display: inline-flex;
	align-items: flex-start;
	max-width: 100%;
`;

export const ProjectCustomFieldHeaderCell = ({
	definition,
	sortBy,
	onSort,
}: {
	definition: CustomFieldColumnDef;
	sortBy: {column: string | null; ascending: boolean};
	onSort: (columnId: string) => void;
}): JSX.Element => {
	const columnId = customFieldColumnId(definition);
	const direction = sortBy.column === columnId ? (sortBy.ascending ? 'ascending' : 'descending') : '';
	return (
		<th
			aria-label={definition.displayName}
			data-cy={`projects-col-cf-${definition.key}-header`}
			className={`sortable ${direction}`}
			onClick={() => onSort(columnId)}
			style={{minWidth: WIDTH, width: WIDTH, maxWidth: WIDTH}}
		>
			<HeaderContent>
				<HeaderName name={definition.displayName} />
				{direction ? <span data-cy="cf-header-sort-indicator">&nbsp;&nbsp;</span> : null}
			</HeaderContent>
		</th>
	);
};

export const ProjectCustomFieldCell = ({
	definition,
	raw,
	path,
	format,
}: {
	definition: CustomFieldColumnDef;
	raw: string | null | undefined;
	path: string;
	format: CustomFieldFormat;
}): JSX.Element => {
	let content: React.ReactNode = null;
	if (definition.kind === 'DATE') {
		const parts = projectCustomFieldDateParts(raw);
		content = parts ? (
			<span>
				<span className="month-day">{parts.monthDay}</span>
				<span className="year">{parts.year}</span>
			</span>
		) : null;
	} else {
		const display = formatCustomFieldValue(definition, raw, format.locale, format.decimalSeparator);
		content = display ? <TruncatedValue value={display} /> : null;
	}
	return <TdLink path={path} dataCy={cellCy(definition)} width={WIDTH} content={content} />;
};

// Connected-project groups have no value of their own: a blank link to the group, like the group row's
// other empty cells.
export const ProjectCustomFieldGroupCell = ({
	definition,
	path,
	style,
}: {
	definition: CustomFieldColumnDef;
	path: string;
	style?: React.CSSProperties;
}): JSX.Element => <TdLink path={path} dataCy={cellCy(definition)} width={WIDTH} style={style} content={''} />;
